import React, { useCallback, useEffect, useState } from "react";
import { ChartBar, Clock, Files, Lightning, ShieldCheck, Subtitles } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";

/**
 * Compact statistics strip shown above Review & Edit.
 * Polls /projects/:id/stats whenever ``segmentsKey`` changes so it stays
 * fresh after edits, reprocess, or new transcriptions.
 */
export default function ProjectStats({ projectId, segmentsKey, activeTrackId }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/stats`);
      setStats(data);
      setErr(null);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) load();
  }, [projectId, segmentsKey, load]);

  if (!stats) {
    return (
      <div className="hard-border bg-white p-5" data-testid="project-stats-loading">
        <div className="overline text-[--muted]">STATISTICS</div>
        <div className="font-mono text-xs text-[--muted] mt-2">{err ? err : "Computing…"}</div>
      </div>
    );
  }

  const t = stats.totals;
  const activeTrack = stats.tracks.find((x) => x.track_id === activeTrackId) || stats.tracks[0] || null;
  const accuracy = activeTrack ? activeTrack.accuracy_percent : t.accuracy_percent;

  return (
    <div className="hard-border bg-white p-6" data-testid="project-stats">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline text-[--muted]">PROJECT STATISTICS</div>
          <h3 className="font-display font-black text-2xl tracking-tighter mt-1">
            {t.minutes.toFixed(1)} min processed · {t.tracks} {t.tracks === 1 ? "track" : "tracks"}
          </h3>
        </div>
        {activeTrack?.engine && (
          <span
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold border border-[--ink] bg-[--paper]"
            data-testid="project-stats-engine"
            title="Speech-to-text engine used for the active track"
          >
            <Lightning weight="bold" size={10} /> {activeTrack.engine}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[--line] border border-[--line]">
        <StatCell icon={Files} label="Words" value={t.words.toLocaleString()} testId="stat-words" />
        <StatCell icon={Subtitles} label="Sentences" value={t.sentences.toLocaleString()} testId="stat-sentences" />
        <StatCell icon={Clock} label="Time saved" value={`${t.minutes_saved.toFixed(1)} min`} hint="vs. typing manually (≈ 4× audio length)" testId="stat-time-saved" />
        <StatCell icon={ChartBar} label="Segments edited" value={`${t.edited_segments} / ${t.segments}`} testId="stat-edited" />
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="overline text-[--muted] inline-flex items-center gap-2">
            <ShieldCheck weight="bold" size={14} /> Accuracy
            {activeTrack && (
              <span className="text-[10px] font-mono normal-case tracking-normal text-[--muted]">· {activeTrack.engine || "—"}</span>
            )}
          </div>
          <span className="font-display font-black text-3xl tracking-tighter tabular-nums" data-testid="accuracy-value">
            {accuracy.toFixed(1)}%
          </span>
        </div>
        <AccuracyBar pct={accuracy} />
        <p className="text-[11px] font-mono text-[--muted] mt-2 leading-relaxed">
          Computed from word-level differences between the original transcription and your edits.
          Higher means fewer manual corrections were needed.
        </p>
      </div>

      {/* Per-track breakdown, only if there's more than one */}
      {stats.tracks.length > 1 && (
        <details className="mt-5 group" data-testid="per-track-breakdown">
          <summary className="cursor-pointer overline text-[--muted] inline-flex items-center gap-1 hover:text-[--ink]">
            Per-track breakdown ({stats.tracks.length})
          </summary>
          <div className="mt-3 border border-[--line] divide-y divide-[--line]">
            {stats.tracks.map((tr) => (
              <div key={tr.track_id} className="p-3 grid grid-cols-12 gap-2 text-xs items-center">
                <div className="col-span-5 truncate font-semibold" title={tr.filename}>{tr.filename || tr.track_id}</div>
                <div className="col-span-2 font-mono text-[--muted]">{tr.engine || "—"}</div>
                <div className="col-span-2 font-mono text-[--muted]">{tr.duration_minutes.toFixed(1)} min</div>
                <div className="col-span-2 font-mono text-[--muted]">{tr.words.toLocaleString()} w</div>
                <div className="col-span-1 text-right font-mono tabular-nums">{tr.accuracy_percent.toFixed(0)}%</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StatCell({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="bg-white px-4 py-3" title={hint || ""} data-testid={testId}>
      <div className="flex items-center gap-2 overline text-[--muted]">
        <Icon weight="bold" size={12} /> {label}
      </div>
      <div className="font-display font-black text-2xl tracking-tighter tabular-nums mt-1">{value}</div>
    </div>
  );
}

/** Horizontal gauge: 0–60 red, 60–85 amber, 85+ green. */
function AccuracyBar({ pct }) {
  const colour =
    pct >= 85 ? "bg-emerald-500"
      : pct >= 60 ? "bg-amber-500"
        : "bg-red-500";
  return (
    <div className="h-3 w-full bg-[--paper] border border-[--ink] relative overflow-hidden" data-testid="accuracy-bar">
      <div
        className={`absolute inset-y-0 left-0 ${colour} transition-[width] duration-500`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
      {/* tick marks */}
      <div className="absolute inset-y-0 left-[60%] w-px bg-[--ink]/30" />
      <div className="absolute inset-y-0 left-[85%] w-px bg-[--ink]/30" />
    </div>
  );
}
